// React
import React, { Fragment, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'
import { compose } from 'redux'

// Components
import Skeleton from 'ui/blocks/Skeleton'
import { HostStyles, Availability } from './styles'

// Shared
import withStore from 'shop-components/utils/withStore'
import withTheme from 'shop-components/utils/withTheme'
import registerCustomElement from 'shop-components/utils/registerCustomElement'
import { withTranslation } from 'shared/utils/withTranslation'
import { fetchAvailabilities } from 'shop-components/redux/actions/availability'
import { hasVariations, isValidBundleVariations } from 'shop-components/utils/variations'

const ProductAvailability = ({
  productId,
  isLoading,
  availability,
  cart,
  fetchProductAvailabilities,
  hideAvailability,
  item,
  setAttribute,
  t
}) => {
  const notSelectedVariant = useMemo(() => {
    if (item?.type === 'bundles') {
      return hasVariations(item) && !isValidBundleVariations(item)
    }
    return hasVariations(item) && !item?.variation_id
  }, [item])

  const showAvailable = availability || notSelectedVariant

  useEffect(() => {
    if (productId && !!cart.startsAt && !!cart.stopsAt && !!cart.startLocationId) {
      fetchProductAvailabilities({
        id: productId,
        startsAt: cart.startsAt,
        stopsAt: cart.stopsAt,
        startLocationId: cart.startLocationId
      })
    }
  }, [cart.startsAt, cart.stopsAt, cart.startLocationId])

  useEffect(() => {
    if (!cart.startsAt || !cart.stopsAt || !showAvailable) {
      setAttribute('visible', false)
    } else {
      setAttribute('visible', true)
    }
  }, [cart.startsAt, cart.stopsAt, showAvailable])

  if (isLoading) return <Skeleton width={150} />

  if (!cart.startsAt || !cart.stopsAt || !showAvailable) return null

  const availableString = hideAvailability || notSelectedVariant ?
    t('user.store.available') :
    `${availability.plannable} ${t('user.store.available')?.toLowerCase?.()}`

  return (
    <Fragment>
      <HostStyles />
      <Availability available={!!availability?.plannable || notSelectedVariant}>
        {availability?.plannable || notSelectedVariant ? availableString : t('user.store.unavailable')}
      </Availability>
    </Fragment>
  )
}

ProductAvailability.displayName = 'ProductAvailability'

ProductAvailability.propTypes = {
  availability: PropTypes.object,
  isLoading: PropTypes.bool,
  hideAvailability: PropTypes.bool,
  item: PropTypes.object,
  cart: PropTypes.object,
  productId: PropTypes.string,
  fetchProductAvailabilities: PropTypes.func,
  setAttribute: PropTypes.func,
  t: PropTypes.func
}

const mapStateToProps = (state, props) => {
  let availability

  const cart = state.orm.carts.getById(state.client.cartId || window.booqableOptions.cart?.id)

  const item = state.items.records[props.productId]

  if (item?.products?.length > 1 && item?.type !== 'bundles') {
    availability = item?.products?.find(({ id }) => id === item.variation_id)?.availability
  } else {
    availability = item?.availability
  }

  return {
    cart: {
      id: cart?.id ?? window.booqableOptions.cart?.id,
      startsAt: cart?.starts_at ?? window.booqableOptions.cart?.from,
      stopsAt: cart?.stops_at ?? window.booqableOptions.cart?.till,
      startLocationId: cart?.start_location_id ?? window.booqableOptions.cart?.startLocationId
    },
    isLoading: state.items.loading && !availability,
    availability,
    hideAvailability: state.settings.get('store.hide_product_availability_quantities'),
    item
  }
}

const mapDispatchToProps = (dispatch) => ({
  fetchProductAvailabilities: (attributes) => dispatch(fetchAvailabilities(attributes))
})

const decoratedComponent = compose(
  withStore,
  withTranslation('user'),
  withTheme,
  reduxConnect(mapStateToProps, mapDispatchToProps)
)(ProductAvailability)

registerCustomElement('bq-product-availability', decoratedComponent, {
  mountElement: 'span',
  attributes: ['product-id']
})

export default decoratedComponent
